import { useState, useMemo, useCallback } from "react";
import {
	ResponsiveContainer,
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
} from "recharts";
import { useTheme } from "@emotion/react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

function CashFlowChart({ data, setClickedItem, setSelectedGroup }) {
	const theme = useTheme();
	const userLanguage = localStorage.getItem("i18nextLng") || "en";
	const userCurrency = localStorage.getItem("userCurrency");
	const [tooltipInfo, setTooltipInfo] = useState(null);
	const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
	const { t } = useTranslation();
	const darkmode = localStorage.getItem("darkMode") === "true";
	const handleMouseOver = (data, barName, e) => {
		const value = data[barName];
		setTooltipInfo({ name: barName, value });
		setTooltipPos({ x: e.clientX, y: e.clientY });
	};

	function handleBarClick(type, dataPoint, e) {
		const periodStr = dataPoint.period;
		let startDate, endDate;

		if (periodStr.includes("-")) {
			const [year, month] = periodStr.split("-").map(Number);
			startDate = new Date(year, month - 1, 1);
			endDate = new Date(year, month, 0);
		} else {
			const year = Number(periodStr);
			startDate = new Date(year, 0, 1);
			endDate = new Date(year, 11, 31);
		}

		setClickedItem({
			type,
			startDate,
			endDate,
		});
		setSelectedGroup(null);
	}

	const handleMouseOut = () => {
		setTooltipInfo(null);
	};
	const maxValue = Math.max(
		...data.map((d) =>
			Math.max(Math.abs(d.income || 0), Math.abs(d.expense || 0))
		),
		0
	).toFixed(2);

	const formatValue = useCallback(
		(value) => {
			return maxValue.toLocaleString().length > 5
				? value.toLocaleString()
				: value.toLocaleString(undefined, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					});
		},
		[maxValue]
	);

	const yAxisWidth = useMemo(() => {
		if (!data?.length) return 80;

		const ctx = document.createElement("canvas").getContext("2d");
		ctx.font = "17.15px Roboto";

		const formatted = formatValue(maxValue);
		const textWidth = ctx.measureText(formatted).width;
		const padding = maxValue.toLocaleString().length > 5 ? 0 : 10;
		return textWidth + padding;
	}, [data, maxValue, formatValue]);

	const formatTick = (value) => {
		const [year, month] = value.split("-");
		if (!month) return year;
		const date = new Date(year, month - 1, 1);
		return new Intl.DateTimeFormat(userLanguage, { month: "short" }).format(
			date
		);
	};

	return (
		<Box
			sx={{
				backgroundColor: theme.palette.background.paper,
				m: 2,
				borderRadius: "10px",
				padding: "16px",
				position: "relative",
			}}
		>
			<Typography
				variant="h6"
				id="tableTitle"
				component="div"
				sx={{ mb: 2 }}
			>
				{t("cashflowGraph")} ({userCurrency})
			</Typography>
			<ResponsiveContainer width="100%" height={400}>
				<BarChart
					data={data}
					margin={{ top: 15, right: 5, bottom: 5, left: 5 }}
				>
					<CartesianGrid
						vertical={false}
						strokeDasharray="5 5"
						opacity={darkmode ? 0.5 : 1}
					/>
					<XAxis
						dataKey="period"
						tickFormatter={formatTick}
						axisLine={false}
						tickLine={false}
						tick={({ x, y, payload }) => {
							const periodStr = payload.value;

							const handleClick = () => {
								let startDate, endDate;

								if (periodStr.includes("-")) {
									const [year, month] = periodStr
										.split("-")
										.map(Number);
									startDate = new Date(year, month - 1, 1);
									endDate = new Date(year, month, 0);
								} else {
									const year = Number(periodStr);
									startDate = new Date(year, 0, 1);
									endDate = new Date(year, 11, 31);
								}

								setClickedItem({
									type: null,
									startDate,
									endDate,
								});
								setSelectedGroup(null);
							};

							return (
								<text
									x={x}
									y={y + 15}
									textAnchor="middle"
									fill={theme.palette.text.secondary}
									style={{
										cursor: "pointer",
										userSelect: "none",
									}}
									onClick={handleClick}
								>
									{formatTick(periodStr)}
								</text>
							);
						}}
					/>
					<YAxis
						tickFormatter={formatValue}
						width={yAxisWidth}
						axisLine={false}
						tickLine={false}
						tick={{
							fill: theme.palette.text.secondary,
						}}
					/>
					<Bar
						dataKey="income"
						fill={theme.palette.values.positive}
						cursor="pointer"
						onMouseOver={(data, index, e) =>
							handleMouseOver(data.payload, "income", e)
						}
						onMouseOut={handleMouseOut}
						radius={[5, 5, 0, 0]}
						onClick={(data, index, e) =>
							handleBarClick("income", data, e)
						}
					/>
					<Bar
						dataKey="expense"
						fill={theme.palette.values.negative}
						cursor="pointer"
						onMouseOver={(data, index, e) =>
							handleMouseOver(data.payload, "expense", e)
						}
						onMouseOut={handleMouseOut}
						radius={[5, 5, 0, 0]}
						onClick={(data, index, e) =>
							handleBarClick("expense", data, e)
						}
					/>
				</BarChart>
			</ResponsiveContainer>

			{tooltipInfo && (
				<div
					style={{
						position: "fixed",
						top: tooltipPos.y + 10,
						left: tooltipPos.x + 10,
						backgroundColor: theme.palette.background.paper,
						color:
							tooltipInfo.name === "income"
								? theme.palette.values.positive
								: theme.palette.values.negative,
						border: `1px solid ${theme.palette.border.main}`,
						padding: "5px",
						pointerEvents: "none",
						zIndex: 1000,
					}}
				>
					<strong>
						{tooltipInfo.value.toFixed(2).toLocaleString()}{" "}
						{userCurrency}
					</strong>
				</div>
			)}
		</Box>
	);
}

export default CashFlowChart;
